import { Component, Input, OnInit } from '@angular/core';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { accountDetailsLabel, Constants, pdfLabelsSubmitDetails, pdfLabelsToDetails, recipientType, transactionDetailLabels, transactionTypes, transactionTypesDict, yesNo } from '../../../shared/utilities/Constants';
import { Store } from '@ngrx/store';
import { selectViewInContext } from '../../../shared/reducers';
import { isWireForm, selectStagedStandingAuthForm, selectStandingAuthWireInfo } from '../../reducers';
import { selectInfo, selectNraIndicator, selectStateCode, selectUseTestTrustDigitalToPdfFlow } from '../../../account-info-header/reducers';
import { StandingAuthReviewPageActions } from '../../actions';
import { WireStandingAuthDetails } from '../../models/standingAuthorizationForm';
import { FormatIsoAddressFieldsService } from '../../../shared/services/format-isoAddressFields.service';


@Component({
  standalone: false,
  selector: 'mm-sa-transaction-details-section-wire-iso',
  templateUrl: './sa-transaction-details-section-wire-iso.component.html',
  styleUrls: ['./sa-transaction-details-section-wire-iso.component.scss']
})
export class SaTransactionDetailsSectionWireIsoComponent implements OnInit {
  @Input() isInrFromTable: boolean;
  readonly labels = transactionDetailLabels;
  readonly pdfLabelsToDetails = pdfLabelsToDetails;
  readonly pdfLabelsSubmitDetails = pdfLabelsSubmitDetails;
  createRequestStep$: Observable<string>;
  destroy$ = new Subject<void>();
  transactionType: string;
  aba: string;
  receivingBankDetails: string[];
  intermediarySelection: string;
  isEscrow: boolean;
  recipient: string;
  receivingAccountHolder: string[];
  intermediaryDetail: string[];
  toAccount: string;
  intermediaryAccountNumber: string;
  intermediaryName: string;
  isIntermediaryYes: boolean;
  isFirstParty: boolean;
  transmissionNotes: string;
  nickname: string;
  isIra: boolean;
  fedTaxPercent: string;
  stateTaxPercent: string;
  recipientName: string[];
  saAbaLabel: string;
  saAbaInfoLabel: string;
  saStateTaxLabel: string = transactionDetailLabels.stateTax;
  intermediaryAccountNumberLabel: string = accountDetailsLabel.accountNumber;
  isIrsAba: boolean = false;
  taxPayerId: string;
  taxTypeCode: string;
  transmissionNotesLabel: string = transactionDetailLabels.wireTransmissionNotes;
  pdfTransmissionNotesLoa: string = pdfLabelsSubmitDetails.additionalWireInstructions;

  constructor(private store: Store, private formatIsoAddressFieldsService: FormatIsoAddressFieldsService) { }

  ngOnInit(): void {
    this.createRequestStep$ = this.store.select(selectViewInContext);
    combineLatest([
      this.store.select(selectStagedStandingAuthForm),
      this.store.select(selectStandingAuthWireInfo),
      this.store.select(selectInfo),
      this.store.select(selectUseTestTrustDigitalToPdfFlow),
      this.store.select(selectStateCode),
      this.store.select(selectNraIndicator)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([form, wireInfo, info, useTestTrustDigitalToPdf, stateCode, nraIndicator]) => {
      if (form.transactionType) {
        this.transactionType = transactionTypesDict[form.transactionType];
        if (form.isSubmitAndCreateBundledSa){
          this.transactionType += ' - bundled';
        }
      }

      if (isWireForm(form)) {
        let details = form.standingAuthorizationDetails;
        this.isEscrow = details.isEscrow;
        this.isFirstParty = details.recipient === recipientType.firstParty;
        this.aba = details.aba;
        this.toAccount = details.toAccount;
        this.transmissionNotes = details.transmissionNotes.trim();
        this.nickname = details.authorizationNickname.trim();
        this.taxPayerId = details.taxPayerId;
        this.taxTypeCode = details.taxTypeCode;

        this.isIra = info.classifications.isIra;
        this.fedTaxPercent = details.fedTaxPercent;
        this.stateTaxPercent = details.stateTaxPercent;
        
        this.receivingBankDetails = [
          details.receivingBankName1,
          details.receivingBankName2,
          details.receivingBankAddress1,
          details.receivingBankAddress2,
          details.receivingBankCountry
        ];

        this.intermediarySelection = details.intermediarySelection;
        this.recipient = this.determineRecipient(details.recipient);

        this.isIntermediaryYes = details.intermediarySelection === yesNo.yes;
        const country = details.intermediaryCountry == "US" ? "UNITED STATES" : details.intermediaryCountry;
        if (this.isIntermediaryYes) {
            this.intermediaryAccountNumber = details.intermediaryAccountNumber;
            this.intermediaryName = details.intermediaryName;
            this.saAbaLabel = transactionDetailLabels.abaNumber;
            this.saAbaInfoLabel = transactionDetailLabels.intermediaryBankInformation;
            this.intermediaryDetail = this.formatIntermediaryAddress(details, country);
        } else {
          this.saAbaLabel = transactionDetailLabels.abaNumber;
          this.saAbaInfoLabel = transactionDetailLabels.intermediaryBankInformation;
        }

        this.recipientName = [
          details.name
        ]

        let beneficiaryCountry = wireInfo.toDetails.beneficiaryCountry;
        if(useTestTrustDigitalToPdf) {
          let country = wireInfo.countryList.find(country => country.code === details.receivingAccountHolderCountry);
          beneficiaryCountry = country?.description?.trim()?.toUpperCase() ?? '';
        }
        this.receivingAccountHolder = this.formatReceivingAccountHolderAddress(details, beneficiaryCountry);

        this.isIrsAba = wireInfo.isIrsAba;
        this.transmissionNotesLabel = this.isIrsAba ? this.labels.wireIrsTransmissionNotes : this.labels.wireTransmissionNotes;
        this.pdfTransmissionNotesLoa = this.isIrsAba ? this.pdfLabelsSubmitDetails.transmissionNotesIrs : this.pdfLabelsSubmitDetails.additionalWireInstructions;
      }

      if (stateCode && !nraIndicator) {
        this.saStateTaxLabel = `${this.labels.stateTax} (${stateCode})`;
      }
    });
  }

  // Formatting and merging receiving account holder address which includes adding ',' only if next field is present which is optional.
  formatReceivingAccountHolderAddress(details: WireStandingAuthDetails, beneficiaryCountry: string) : string[] {
    let receivingAccountHolderAdress = [];
    receivingAccountHolderAdress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderStreetNumber ? details.receivingAccountHolderStreetNumber + " " + details.receivingAccountHolderStreetName : details.receivingAccountHolderStreetName) +
      (this.formatIsoAddressFieldsService.getNextLine(details.receivingAccountHolderStreetNumber ? details.receivingAccountHolderStreetNumber + " " + details.receivingAccountHolderStreetName : details.receivingAccountHolderStreetName)) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderApartmentSuiteEtc, details.receivingAccountHolderDepartment, details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderDepartment, details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderFloor, "") +
      (this.formatIsoAddressFieldsService.getNextLine(details.receivingAccountHolderApartmentSuiteEtc, details.receivingAccountHolderDepartment, details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor)) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderTownLocationName, details.receivingAccountHolderCity, details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderCity, details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderZip) +
      (this.formatIsoAddressFieldsService.getNextLine(details.receivingAccountHolderTownLocationName, details.receivingAccountHolderCity, details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip)) +
      beneficiaryCountry); 
    return receivingAccountHolderAdress;
  }

  // Formatting and intermediary merging address which includes adding ',' only if next field is present which is optional.
  formatIntermediaryAddress(details: WireStandingAuthDetails, country: string): string[] {
    let intermediaryAddress = [];
    intermediaryAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryName) +
    (this.formatIsoAddressFieldsService.getNextLine(details.intermediaryName)) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryStreetNumber ? details.intermediaryStreetNumber + " " + details.intermediaryStreetName : details.intermediaryStreetName) +
    (this.formatIsoAddressFieldsService.getNextLine(details.intermediaryStreetNumber ? details.intermediaryStreetNumber + " " + details.intermediaryStreetName : details.intermediaryStreetName)) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryApartmentSuiteEtc, details.intermediaryDepartment, details.intermediaryBuildingName, details.intermediaryFloor) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryDepartment, details.intermediaryBuildingName, details.intermediaryFloor) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryBuildingName, details.intermediaryFloor) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryFloor, "") +
    (this.formatIsoAddressFieldsService.getNextLine(details.intermediaryApartmentSuiteEtc, details.intermediaryDepartment, details.intermediaryBuildingName, details.intermediaryFloor)) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryCity, details.intermediaryState, details.intermediaryZip) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryState, details.intermediaryZip) +
    this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryZip,"") +    
    this.formatIsoAddressFieldsService.getNextLine(details.intermediaryCity, details.intermediaryState, details.intermediaryZip, country) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(country))
    return intermediaryAddress;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEditClicked() {
    this.store.dispatch(StandingAuthReviewPageActions.editClicked());
  }

  determineRecipient(recipient: string): string {
    if (recipient === recipientType.firstParty) {
      return Constants.wireFirstParty;
    }
    if (this.isEscrow) {
      return Constants.wireThirdPartyEscrow;
    }
    return Constants.wireThirdParty
  }
}
